import PrepaidCardApi from '@apis/prepaid/PrepaidCardApi';
import userPrepaidCardQueries from '@apis/queries/userPrepaidCardQueries';
import AppModal from '@components/AppModal';
import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import setUserPrepaidCardList from '@hooks/prepaidCard/setUserPrepaidCardList';
import { ErrorResponse } from '@models/error';
import { TransferablePrepaidCardsResponse } from '@models/prepaidCard';
import { ActivityComponentType, useFlow } from '@stackflow/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { setIsPrepaidCardBalanceUpdated } from '@utils/prepaidCardUtils';
import { AxiosError } from 'axios';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface Params {
    prepaidCardId: string;
    targetCardId: string;
}

declare module '@stackflow/config' {
    interface Register {
        TransferConfirmModal: Params;
    }
}

const TransferConfirmModal: ActivityComponentType<'TransferConfirmModal'> = ({ params }) => {
    const { prepaidCardId: sourceCardId, targetCardId } = params;
    const { t } = useTranslation('prepaidCard');
    const { pop } = useFlow();
    const queryClient = useQueryClient();
    const pending = useRef(false);
    const [isPending, setIsPending] = useState(false);
    const { data: source } = useQuery(userPrepaidCardQueries.detail(sourceCardId));
    const { data: transferable } = useQuery(userPrepaidCardQueries.transferable(sourceCardId));
    const target = transferable?.cards.find((card) => card.id === targetCardId);

    if (!source || !target) return null;

    const sourceBalance = source.balance;
    const afterBalance = target.balance + sourceBalance;

    const handleTransfer = () => {
        if (pending.current) return;
        if (sourceBalance <= 0) {
            toast.error(t('transfer.zeroBalance'));
            return;
        }
        if (sourceBalance > target.remainingChargeLimit) {
            toast.error(t('transfer.limitExceeded'));
            return;
        }

        pending.current = true;
        setIsPending(true);
        PrepaidCardApi.transferBalance(sourceCardId, { targetCardId: target.id })
            .then(() => {
                setUserPrepaidCardList(queryClient, (cards) => cards.map((card) => {
                    if (card.id === sourceCardId) return { ...card, balance: 0 };
                    if (card.id === target.id) return { ...card, balance: card.balance + sourceBalance };
                    return card;
                }));
                // 이 화면 타깃 리스트는 transferable 쿼리 — list와 별도라 같이 맞춰준다.
                queryClient.setQueryData<TransferablePrepaidCardsResponse>(
                    userPrepaidCardQueries.transferable(sourceCardId).queryKey,
                    (prev) => (prev ? {
                        ...prev,
                        cards: prev.cards.map((card) => (
                            card.id === target.id
                                ? {
                                    ...card,
                                    balance: card.balance + sourceBalance,
                                    remainingChargeLimit: Math.max(0, card.remainingChargeLimit - sourceBalance),
                                }
                                : card
                        )),
                    } : prev),
                );
                queryClient.invalidateQueries({ queryKey: userPrepaidCardQueries.transferable._def });
                toast.success(t('transfer.success'));
                setIsPrepaidCardBalanceUpdated(true);
                pop(2);
            })
            .catch((e: AxiosError<ErrorResponse>) => {
                toast.error(e.response?.data.message || t('transfer.error'));
            }).finally(() => {
                pending.current = false;
                setIsPending(false);
            });
    };

    return (
        <AppModal title={t('transfer.confirm')}
            deny={t('transfer.cancel')}
            confirm={t('transfer.submit')}
            onConfirm={handleTransfer}
            confirmDisabled={isPending}
            denyDisabled={isPending}
            disableOutside={isPending} >
            <Summary fullWidth spacing={3} align='center'>
                <Image src={target.imageUrl}
                    width={60}
                    height={40}
                    alt={target.name}
                    css={css`
                        border-radius: 8px;
                    `}
                />
                <Column spacing={1} align='flex-start'>
                    <Text size='body-short' fontWeight='medium' align='left' multiline>
                        {target.name}
                    </Text>
                    <Text size='body-long' color={colors.textSecondary} fontWeight='medium' align='left'>
                        {t('transfer.afterBalance', { amount: afterBalance.toLocaleString() })}
                    </Text>
                </Column>
            </Summary>
        </AppModal>
    );
};

const Summary = styled(Row)`
    background-color: var(--color-white);
    border: 1px solid var(--color-gray2);
    border-radius: 8px;
    padding: 16px;
`;
export default TransferConfirmModal;
